import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { Eye, EyeOff } from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useFormSafety } from "@/lib/navigation/useFormSafety";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateProfilePasswordMutation } from "@/modules/profile/services/useUpdateProfilePasswordMutation";
import { Button, Field, Input } from "@/shared/rd";
import { ProfileDiscardModal, ProfileSaveBar, ProfileSaveError } from "./ProfileSaveBar";

type Values = { currentPassword: string; nextPassword: string };

/** Change your own password: the current one, then a new one of 8+ characters. */
export function ProfilePasswordForm({ onSaved }: { onSaved: (message: string) => void }) {
  const t = useTranslations();
  const formId = useId();
  const [error, setError] = useState<string | null>(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNextPassword, setShowNextPassword] = useState(false);
  const { mutateAsync: updateProfilePassword } = useUpdateProfilePasswordMutation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<Values>({ defaultValues: { currentPassword: "", nextPassword: "" } });

  const resetForm = () => {
    reset();
    setError(null);
    setShowCurrentPassword(false);
    setShowNextPassword(false);
  };
  const { promptDiscard, discardDialogState } = useFormSafety({ isDirty, onDiscard: resetForm });

  const onSubmit = handleSubmit(async (values) => {
    // The banner of an earlier failure stays (its Try again shows the spinner) until this save succeeds.
    try {
      await updateProfilePassword(values);
    } catch (caught) {
      setError(
        caught instanceof ApiJsonClientError ? caught.message || t(keys.errors.generic) : t(keys.errors.generic),
      );
      return;
    }
    resetForm();
    onSaved(t("admin.toastPasswordChanged"));
  });

  const reveal = (shown: boolean, toggle: () => void) => (
    <Button
      variant="ghost"
      size="sm"
      iconOnly
      icon={shown ? EyeOff : Eye}
      title={shown ? t(keys.common.hide) : t(keys.common.show)}
      aria-pressed={shown}
      onClick={toggle}
      style={{ width: 24, height: 24 }}
    />
  );

  return (
    <>
      <form id={formId} onSubmit={onSubmit} noValidate style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {error ? (
          <ProfileSaveError title={t("admin.passwordSaveFailed")} message={error} formId={formId} retrying={isSubmitting} />
        ) : null}
        <Field
          layout="row"
          label={t(keys.admin.currentPasswordLabel)}
          htmlFor="profile-current-password"
          error={errors.currentPassword?.message}
        >
          <Input
            id="profile-current-password"
            type={showCurrentPassword ? "text" : "password"}
            autoComplete="current-password"
            placeholder={t(keys.placeholders.password)}
            required
            invalid={Boolean(errors.currentPassword)}
            suffix={reveal(showCurrentPassword, () => setShowCurrentPassword((value) => !value))}
            {...register("currentPassword", { required: t(keys.errors.required) })}
          />
        </Field>
        <Field
          layout="row"
          label={t(keys.admin.newPasswordLabel)}
          htmlFor="profile-next-password"
          help={t(keys.auth.passwordHint)}
          error={errors.nextPassword?.message}
        >
          <Input
            id="profile-next-password"
            type={showNextPassword ? "text" : "password"}
            autoComplete="new-password"
            placeholder={t(keys.placeholders.password)}
            required
            invalid={Boolean(errors.nextPassword)}
            suffix={reveal(showNextPassword, () => setShowNextPassword((value) => !value))}
            {...register("nextPassword", {
              required: t(keys.errors.required),
              minLength: { value: 8, message: t(keys.auth.passwordHint) },
            })}
          />
        </Field>
      </form>
      <ProfileSaveBar
        formId={formId}
        visible={isDirty || isSubmitting}
        saving={isSubmitting}
        onDiscard={() => promptDiscard(resetForm)}
      />
      <ProfileDiscardModal {...discardDialogState} />
    </>
  );
}
